import cx from 'classnames';
import { isEmpty, isNil } from 'lodash';
import { Component } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { compose, withProps } from 'recompose';
import { ELSModalServiceType } from 'models/els';
import { UserRole } from 'models/enum';
import { SimChartAssignment } from 'models/ui';
import { AppConstant, Locales } from 'constants/app.constant';
import { cssHelper } from 'helpers';
import { resetEmptyEhrDemoStorage } from 'helpers/assignment.helper';
import { assignmentService, caseStudyService, featureService, navigationService } from 'services';
import { appActions, appSelectors } from 'redux/ducks/app';
import { instructorActions } from 'redux/ducks/instructor';
import { studentSelectors } from 'redux/ducks/student';
import { AlertModal, Box } from 'components/common';
import AssignmentInstruction from 'components/common/assignment-instruction/AssignmentInstruction';
import AssignmentObjective from 'components/common/assignment-objective/AssignmentObjective';
import { ELSButton, ELSPageLoader, ELSWithModalService } from 'components/common/els';
import BlankChartingCompetency from '../blank-charting-competency/BlankChartingCompetency';

export interface BlankChartingAssignmentProps {
  params: { assignmentId: string };
  locale: Locales;
  fullAssignment: SimChartAssignment & {
    instructions: string;
  };
  setEmptyEhrDemo: Function;
  setEmptyEhrDemoInstructions: Function;
  setEmptyEhrDemoObjectives: Function;
  setIsAssignmentFromSubmission: Function;
  setIsInstructorSaveData: Function;
  setIsInstructorSaveDataUS: Function;
  isInstructorSaveData: boolean;
  isInstructorSaveDataUS: boolean;
  userRole: string;
  loggedInUserId: number;
  modalService: ELSModalServiceType;
}

interface BlankChartingAssignmentState {
  isLoading: boolean;
  objectives: string;
  instructions: string;
}

class BlankChartingAssignment extends Component<BlankChartingAssignmentProps, BlankChartingAssignmentState> {
  static displayName = 'BlankChartingAssignment';

  assignmentId: number;

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      objectives: '',
      instructions: ''
    };

    const { assignmentId } = this.props.params;
    this.assignmentId = Number(assignmentId);
  }

  componentDidMount() {
    resetEmptyEhrDemoStorage();
    if (this.props.locale === Locales.EN_US) {
      this.checkFeatureInstructorSaveDataUSFlag();
    } else {
      this.checkFeatureInstructorSaveDataFlag();
    }
    if (isEmpty(this.props.fullAssignment)) {
      assignmentService.getSimChartAssignment(this.assignmentId).then(({ data }) => this.updateDataEhrDemo(data));
    } else {
      this.updateDataEhrDemo(this.props.fullAssignment);
    }
  }

  checkFeatureInstructorSaveDataFlag = async () => {
    const { setIsInstructorSaveData } = this.props;
    try {
      const isInstructorSaveDataFlag = await featureService.isInstructorSaveDataEnabled();
      setIsInstructorSaveData(isInstructorSaveDataFlag);
    } catch (err) {
      console.error('checkFeatureInstructorSaveDataFlag failed:', err);
    }
  };

  checkFeatureInstructorSaveDataUSFlag = async () => {
    const { setIsInstructorSaveDataUS } = this.props;
    try {
      const isInstructorSaveDataUSFlag = await featureService.isInstructorSaveDataUSEnabled();
      setIsInstructorSaveDataUS(isInstructorSaveDataUSFlag);
    } catch (err) {
      console.error('checkFeatureInstructorSaveDataUSFlag failed:', err);
    }
  };

  handleInstructorSetUpData = async () => {
    const instructorAssignment = await assignmentService.getAssignmentByInstructorId(this.assignmentId, this.props.loggedInUserId).then((res) => res.data);
    if (!isNil(instructorAssignment)) {
      const localStorageKey = this.props.locale === Locales.EN_US ? AppConstant.IS_INSTRUCTOR_SAVE_DATA_US : AppConstant.IS_INSTRUCTOR_SAVE_DATA;
      localStorage.setItem(localStorageKey, 'true');
      navigationService.navigateToClinicalEntryPoint(instructorAssignment.assignmentId);
    } else {
      const systemName = this.props.locale === Locales.EN_US ? 'EHR' : 'EMR';
      this.props.modalService.openModal({
        modalId: 'alertAssignmentInsSaveData',
        content: (
          <AlertModal
            header={`Unable to Add Data to ${systemName}`}
            message={`Something went wrong while adding data to the ${systemName}. Please try again, or contact support if the issue persists.`}
            onOkClick={() => this.props.modalService.closeModal('alertAssignmentInsSaveData')}
          />
        )
      });
    }
  };

  updateDataEhrDemo = (fullAssignment) => {
    this.props.setEmptyEhrDemoInstructions(fullAssignment?.instructions);
    this.props.setEmptyEhrDemoObjectives(fullAssignment?.objectives);
    this.setState({ objectives: fullAssignment.objectives, instructions: fullAssignment.instructions, isLoading: false });
  };

  handleStartDemoClick = async () => {
    const assessment = await caseStudyService.prepareDemoCaseStudy({ caseStudyId: null }).then((res) => res.data);
    if (assessment) {
      this.props.setEmptyEhrDemo({ assessment, patientInfo: null });
      this.props.setIsAssignmentFromSubmission(false);
      localStorage.removeItem(AppConstant.IS_INSTRUCTOR_SAVE_DATA);
      localStorage.removeItem(AppConstant.IS_INSTRUCTOR_SAVE_DATA_US);
      navigationService.navigateToClinicalEntryPoint(assessment.demoAssignmentId);
    }
  };

  render() {
    const shouldShowAddData =
      (!!this.props.isInstructorSaveData && this.props.userRole === UserRole.INSTRUCTOR && this.props.locale === Locales.EN_AU) ||
      (!!this.props.isInstructorSaveDataUS && this.props.userRole === UserRole.INSTRUCTOR && this.props.locale === Locales.EN_US);
    const responsiveClass = cssHelper.mapElsCss({
      els: 'padding-left-3x padding-right-3x',
      md: 'padding-left-1x1o2 padding-right-1x1o2',
      lg: 'padding-left-2x padding-right-2x'
    });

    return (
      <>
        {this.state.isLoading && <ELSPageLoader />}
        <Box px3 py2>
          <h1>{this.props.locale === Locales.EN_US ? 'Empty EHR Information' : 'Empty EMR Information'}</h1>
        </Box>
        <Box py2 className={cx('u-els-background-color-n0', responsiveClass)}>
          <Box p2 className="u-els-background-color-white">
            <h2>Assignment Objectives</h2>
            <AssignmentObjective objective={this.state.objectives} />
          </Box>
          <Box p2 className="u-els-background-color-white">
            <h2>Assignment Instructions</h2>
            <AssignmentInstruction instruction={this.state.instructions} />
          </Box>
        </Box>
        <BlankChartingCompetency />
        <Box className={responsiveClass} py2>
          <ELSButton text="Start Demo" onClick={this.handleStartDemoClick} />
          {shouldShowAddData && (
            <ELSButton
              className="u-els-margin-left-3x"
              text={this.props.locale === Locales.EN_US ? 'Add data to EHR' : 'Add data to EMR'}
              onClick={this.handleInstructorSetUpData}
            />
          )}
        </Box>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  locale: appSelectors.getLocale(state),
  fullAssignment: studentSelectors.getFullAssignment(state),
  userRole: appSelectors.getUserRole(state),
  isInstructorSaveData: appSelectors.getInstructorSaveData(state),
  isInstructorSaveDataUS: appSelectors.getInstructorSaveDataUS(state),
  loggedInUserId: appSelectors.getUserId(state)
});

const mapDispatchToProps = (dispatch) => ({
  setEmptyEhrDemo: (payload) => dispatch(instructorActions.setEmptyEhrDemo(payload)),
  setEmptyEhrDemoInstructions: (instructions) => dispatch(instructorActions.setEmptyEhrDemoInstructions(instructions)),
  setEmptyEhrDemoObjectives: (objectives) => dispatch(instructorActions.setEmptyEhrDemoObjectives(objectives)),
  setIsAssignmentFromSubmission: (payload) => dispatch(appActions.setIsAssignmentFromSubmission(payload)),
  setIsInstructorSaveData: (flagValue: boolean) => dispatch(appActions.setIsInstructorSaveData(flagValue)),
  setIsInstructorSaveDataUS: (flagValue: boolean) => dispatch(appActions.setIsInstructorSaveDataUS(flagValue))
});

export { BlankChartingAssignment as BaseBlankChartingAssignment };
export default compose(
  withProps(() => ({
    params: useParams()
  })),
  ELSWithModalService,
  connect(mapStateToProps, mapDispatchToProps)
)(BlankChartingAssignment);
